import { TransformUniqueValuesByColumnItem } from 'libs/end-to-end/src/types/databases/transform/transform-insights';
import { FilterSetDefinitions } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { FocusAreaDefinition } from 'libs/end-to-end/src/types/databases/focus-areas/focus-area-definition';
import { MetricDefinition } from 'libs/end-to-end/src/types/databases/metrics/metric-definition';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { isBlankString } from 'libs/end-to-end/src/generic';
import { MyMetricStaticControlVariableDefinition } from 'libs/end-to-end/src/my-metric-static-control-variable-definition';

/**
 * When displaying Deep Dive and Top opportunities descriptions, we have a list of Similar Qualities that
 * are shown in a modal when the user clicks on the "See Similar Qualities" button. This is simply meant to
 * be a list of control variables used when calculating disparities and biases. The problem is that we also
 * have hardcoded variables used as control variables that aren't configured in the platform per focus area.
 * In order to properly reflect the control variables used in the calculation, we need to convert the static
 * control variables into a format that can be used in the modal. This function uses a list of prebuilt
 * requirements to properly reflect control variables that are hardcoded. Some of this logic can be found
 * in the python scripts called create_skill_vars and also calculate_controlled_disparities. The requirements
 * can be found in this ticket: https://sigmasquaredinc.atlassian.net/browse/S2-3424
 */
export const convertStaticControlVariableDefinitions = ({
  uniqueValuesByColumn,
  filterSetDefinitions,
  focusAreaDefinition,
  performanceMetric,
}: {
  uniqueValuesByColumn: TransformUniqueValuesByColumnItem[];
  filterSetDefinitions: FilterSetDefinitions;
  focusAreaDefinition: FocusAreaDefinition;
  performanceMetric: MetricDefinition | null;
}): MyMetricStaticControlVariableDefinition[] => {
  const staticControlVariables: MyMetricStaticControlVariableDefinition[] = [];

  const allFilterDefinitions = filterSetDefinitions.flatMap(
    (filterSetDefinition) => filterSetDefinition.filterDefinitions,
  );

  // The department column is hardcoded as a confounding variable in the calculation of disparities and biases.
  // This is used as a control variable as a means to only compare people or stops from the same department.
  const departmentVarValues =
    uniqueValuesByColumn
      .find((item) => item.columnName === 'department')
      ?.uniqueValues.filter((value) => !isBlankString(value)) ?? [];

  // We should only add it as a static var if there is more than one department.
  // If there aren't then we don't need to display it.
  if (departmentVarValues.length > 1) {
    // This department field is hardcoded but sometimes it doesn't actually refer to a department.
    // It could be referred to as something else. We can use filter definitions to infer a displayable name
    // for that column. If it isn't a filter, we will then default to department
    const name = allFilterDefinitions.find(
      (filterDefinition) => filterDefinition.columnHeaderValue === 'department',
    )?.displayName;
    staticControlVariables.push({
      columnHeaderValue: 'department',
      name: name ?? 'Department',
    });
  }

  // The location column is hardcoded as a confounding variable in the calculation of disparities and biases.
  // This is used as a control variable as a means to only compare people or stops from the same location.
  const locationVarValues =
    uniqueValuesByColumn
      .find((item) => item.columnName === 'location')
      ?.uniqueValues.filter((value) => !isBlankString(value)) ?? [];

  // We should only add it as a static var if there is more than one location.
  // If there aren't then we don't need to display it.
  if (locationVarValues.length > 1) {
    // This location field is hardcoded but sometimes it doesn't actually refer to a location.
    // It could be referred to as something else. We can use filter definitions to infer a displayable name
    const name = allFilterDefinitions.find(
      (filterDefinition) => filterDefinition.columnHeaderValue === 'location',
    )?.displayName;
    staticControlVariables.push({
      columnHeaderValue: 'location',
      name: name ?? 'Location',
    });
  }

  // The job_level column is hardcoded as a confounding variable in the calculation of disparities and biases.
  // This is used as a control variable as a means to only compare people or stops from the same job level.
  const jobLevelVarValues =
    uniqueValuesByColumn
      .find((item) => item.columnName === 'job_level')
      ?.uniqueValues.filter((value) => !isBlankString(value)) ?? [];

  // We should only add it as a static var if there is more than one job level.
  // If there aren't then we don't need to display it.
  if (jobLevelVarValues.length > 1) {
    // This job level field is hardcoded but sometimes it doesn't actually refer to a job level.
    // It could be referred to as something else. We can use filter definitions to infer a displayable name
    staticControlVariables.push({
      columnHeaderValue: 'job_level',
      name: 'Job Level',
    });
  }

  // The experience column is hardcoded as a skill variable in the calculation of disparities and biases.
  // The reason we can't add this as a configurable control variable right away is that there is no logic
  // to deduplicate the dynamic with the static control variables in the python scripts.
  const isExperienceDynamicVar = focusAreaDefinition.controlVariables.find(
    (controlVariable) => controlVariable.columnHeaderValue === 'experience',
  );

  // We should only add it as a static var if it isn't already a dynamic control variable.
  if (!isExperienceDynamicVar) {
    const experienceVarValues =
      uniqueValuesByColumn
        .find((item) => item.columnName === 'experience')
        ?.uniqueValues.filter((value) => !isBlankString(value)) ?? [];
    // We should only add it as a static var if there is more than one experience.
    // If there aren't then we don't need to display it.
    if (experienceVarValues.length > 1) {
      staticControlVariables.push({
        columnHeaderValue: 'experience',
        name: 'Experience',
      });
    }
  }

  const isAgeDynamicVar = focusAreaDefinition.controlVariables.find(
    (controlVariable) => controlVariable.columnHeaderValue === 'age',
  );

  if (!isAgeDynamicVar) {
    const ageVarValues =
      uniqueValuesByColumn
        .find((item) => item.columnName === 'age')
        ?.uniqueValues.filter((value) => !isBlankString(value)) ?? [];
    // We should only add it as a static var if there is more than one age.
    // If there aren't then we don't need to display it.
    if (ageVarValues.length > 1) {
      staticControlVariables.push({
        columnHeaderValue: 'age',
        name: 'Age',
      });
    }
  }

  switch (focusAreaDefinition.focusArea) {
    case 'Compensation':
    case 'Attrition':
    case 'Promotion':
      // For compensation, attrition, and promotion, we have a tenure column that is hardcoded as a control variable
      // and is produced in produce_panel_data. The only way we can add this as a control variable is if entry_date
      // and leave_date exist. We then need to make sure that there are more than one unique value for each of these
      // columns.
      const entryDateValues =
        uniqueValuesByColumn
          .find((item) => item.columnName === 'entry_date')
          ?.uniqueValues.filter((value) => !isBlankString(value)) ?? [];
      const leaveDateValues =
        uniqueValuesByColumn
          .find((item) => item.columnName === 'leave_date')
          ?.uniqueValues.filter((value) => !isBlankString(value)) ?? [];

      // We should only add it as a static var if there is more than one entry date and leave date.
      // If there aren't then we don't need to display it.
      if (entryDateValues.length > 1 && leaveDateValues.length > 1) {
        staticControlVariables.push({
          columnHeaderValue: 'tenure',
          name: 'Tenure',
        });
      }

      // For compensation, attrition, and promotion, their preferred performance metric is also recognized as
      // a control variable. We need to make sure that there are more than one unique value for the performance metric.
      const performanceMetricValues =
        uniqueValuesByColumn
          .find(
            (item) => item.columnName === performanceMetric?.columnHeaderValue,
          )
          ?.uniqueValues.filter((value) => !isBlankString(value)) ?? [];

      // We should only add it as a static var if there is more than one performance metric.
      // If there aren't then we don't need to display it
      if (performanceMetricValues.length > 1 && performanceMetric) {
        staticControlVariables.push({
          columnHeaderValue: performanceMetric.columnHeaderValue,
          name: performanceMetric.displayName,
        });
      }

      break;
    case 'Hiring':
    case 'Performance':
    case 'Engagement':
    case 'Leadership':
      break;
    default:
      throw guardExhaustive(focusAreaDefinition.focusArea);
  }

  switch (focusAreaDefinition.focusArea) {
    case 'Compensation':
      // For compensation, entry year is hardcoded as a control variable.
      // This is used as a control variable as a means to only compare people or stops from the same entry year.
      // as a means of accounting for inflation
      const entryDateValues =
        uniqueValuesByColumn
          .find((item) => item.columnName === 'entry_date')
          ?.uniqueValues.filter((value) => !isBlankString(value)) ?? [];

      const entryYearValues =
        uniqueValuesByColumn
          .find((item) => item.columnName === 'entry_year')
          ?.uniqueValues.filter((value) => !isBlankString(value)) ?? [];

      if (entryDateValues.length > 1 || entryYearValues.length > 1) {
        staticControlVariables.push({
          columnHeaderValue: 'entry_year',
          name: 'Entry Year',
        });
      }
      break;
    case 'Attrition':
    case 'Promotion':
    case 'Hiring':
    case 'Performance':
    case 'Engagement':
    case 'Leadership':
      break;
    default:
      throw guardExhaustive(focusAreaDefinition.focusArea);
  }

  return staticControlVariables;
};
